import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';

export interface Backup {
  id: string;
  name?: string;
  uid?: string;
  type?: string;
  /** '1' when persistent. */
  persistent?: string;
  /** Size in MB (string from the API). */
  size?: string | number;
  /** null → full backup; otherwise incremental. */
  backup_increments?: unknown;
  state?: string;
  created_at?: string | number;
  attached?: { name: string; instance_id: string; backup_disks?: (string | number)[] };
}

export type BackupRestoreMode = 'clone' | 'restore';
export interface RestoreBackupPayload {
  type: BackupRestoreMode;
  // For `clone` the backend requires a flavor AND at least one network — it
  // instantiates a fresh instance and raises Network.Invalid on an empty list.
  instance?: { name?: string; flavor?: string; networks?: { id: string }[] };
}

export function useBackupList(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.backups(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ backups?: Backup[] } | Backup[]>(
        await serviceApi.get(onPath(serviceId!, 'backups')),
      );
      return Array.isArray(body) ? body : body.backups ?? [];
    },
  });
}

export function useBackupDetail(serviceId: string | undefined, backupId: string | undefined) {
  return useQuery({
    queryKey: onKeys.backup(serviceId ?? '', backupId ?? ''),
    enabled: !!serviceId && !!backupId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ backup?: Backup } | Backup>(
        await serviceApi.get(onPath(serviceId!, `backups/${backupId}`)),
      );
      return (body && typeof body === 'object' && 'backup' in body ? body.backup : body) as Backup;
    },
  });
}

export function useCreateBackup(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, Record<string, unknown> | void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload?: Record<string, unknown>) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/backups`), payload ?? {})).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.backups(serviceId) });
      // Creating a backup transitions the instance into the BACKUP state — refetch
      // its detail so the in-progress banner shows and the self-driving poll (which
      // keys off `state === 'BACKUP'`) takes over until the backup settles.
      qc.invalidateQueries({ queryKey: onKeys.instance(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteBackup(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (backupId: string) =>
      (await serviceApi.delete(onPath(serviceId, `backups/${backupId}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.backups(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}

/** Restore a backup — `clone` to a new instance, or `restore` in-place. */
export function useRestoreBackup(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { backupId: string; payload: RestoreBackupPayload }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ backupId, payload }) =>
      (await serviceApi.post(onPath(serviceId, `backups/${backupId}/restore`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.backups(serviceId) });
      qc.invalidateQueries({ queryKey: onKeys.instances(serviceId) });
      options?.onSuccess?.(...a);
    },
  });
}
